import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtCurrency, fmtNumber, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge } from "@/components/dash/ui";

const SORTS: { key: string; label: string }[] = [
  { key: "spend", label: "Total spend" },
  { key: "ltv", label: "Est. LTV" },
  { key: "active", label: "Last activity" },
  { key: "services", label: "Services used" },
  { key: "support", label: "Support" },
];

export default function CustomersPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [sort, setSort] = useState("spend");
  const [multi, setMulti] = useState(false);
  const q = useQuery({ queryKey: ["customers", sort, multi], queryFn: () => dashApi.customers(sort, 200, multi) });
  const ingest = useMutation({ mutationFn: dashApi.customerIngest, onSuccess: () => qc.invalidateQueries() });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Unified customers matched across Odoo, Repairs, PrestaShop, IronChat and IronPayments. Spend = Odoo + ecommerce + subscriptions (repairs invoiced into Odoo are shown separately)."
        right={isAdmin && (
          <button type="button" onClick={() => ingest.mutate()} disabled={ingest.isPending}
            className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-700 disabled:opacity-60">
            {ingest.isPending ? "Refreshing…" : ingest.isSuccess ? "Refreshed ✓" : "Refresh match"}
          </button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2">
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
          {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} /> Multi-service only
        </label>
      </div>
      <Panel title={`Customers (${q.data?.customers.length ?? 0})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Customer</th>
                <th className="pb-2 text-right font-medium">Spend</th>
                <th className="pb-2 text-right font-medium">Est. LTV</th>
                <th className="pb-2 text-right font-medium">Services</th>
                <th className="pb-2 text-right font-medium">Repairs</th>
                <th className="pb-2 text-right font-medium">Support</th>
                <th className="pb-2 text-right font-medium">Open</th>
                <th className="pb-2 text-right font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {q.data?.customers.map((c) => (
                <tr key={c.id} className="text-slate-300 hover:bg-ink-700/40">
                  <td className="py-2">
                    <Link to={`/customers/${c.id}`} className="text-slate-100 hover:text-brand-300">{c.name}</Link>
                    <div className="text-[11px] text-slate-600">{c.email ?? "no email"}</div>
                  </td>
                  <td className="py-2 text-right tabular-nums text-white">{fmtCurrency(c.total_spend)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(c.ltv_estimate)}</td>
                  <td className="py-2 text-right tabular-nums">{c.services_used}</td>
                  <td className="py-2 text-right tabular-nums">{fmtNumber(c.repair_count)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtNumber(c.support_count)}</td>
                  <td className={`py-2 text-right tabular-nums ${c.open_issues ? "text-amber-300" : "text-slate-500"}`}>{c.open_issues}</td>
                  <td className="py-2 text-right text-[11px] text-slate-500">{fmtRelative(c.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {isAdmin && <ReviewQueue />}
    </div>
  );
}

function ReviewQueue() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customer-review"], queryFn: dashApi.customerReview });
  const invalidate = () => qc.invalidateQueries();
  const confirm = useMutation({ mutationFn: dashApi.customerConfirm, onSuccess: invalidate });
  const reject = useMutation({ mutationFn: dashApi.customerReject, onSuccess: invalidate });
  const items = q.data?.items ?? [];

  return (
    <Panel title="Match review" subtitle="Low-confidence matches. Confirm to lock a link, or reject to split into its own customer.">
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !items.length ? (
        <EmptyState compact>Nothing awaiting review — all matches are high-confidence.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">System</th>
              <th className="pb-2 font-medium">Record</th>
              <th className="pb-2 text-right font-medium">Confidence</th>
              <th className="pb-2 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-500/40">
            {items.map((it) => (
              <tr key={it.id} className="text-slate-300">
                <td className="py-2"><SourceBadge code={it.source} /></td>
                <td className="py-2">
                  <span className="text-slate-200">{it.name || "—"}</span>
                  <div className="text-[11px] text-slate-600">{it.email ?? "no email"}</div>
                </td>
                <td className="py-2 text-right tabular-nums text-slate-400">{Math.round(it.confidence * 100)}%</td>
                <td className="py-2 text-right">
                  <div className="inline-flex gap-1.5">
                    <button type="button" onClick={() => confirm.mutate(it.id)} disabled={confirm.isPending}
                      className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50">Confirm</button>
                    <button type="button" onClick={() => reject.mutate(it.id)} disabled={reject.isPending}
                      className="rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/20 disabled:opacity-50">Split</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
